<script lang="ts">
  import type { Snippet } from "svelte"

  type Props = {
    class?: string
    children: Snippet
  }

  let { class: className = "", children }: Props = $props()

  let wrapper: HTMLDivElement | undefined = $state()
  let canScrollLeft = $state(false)
  let canScrollRight = $state(false)

  $effect(() => {
    if (!wrapper) return
    const root = wrapper.querySelector<HTMLElement>('[role="table"]')
    if (!root) return
    const update = () => {
      canScrollLeft = root.scrollLeft > 0
      canScrollRight = root.scrollLeft + root.clientWidth < root.scrollWidth - 1
    }
    // MutationObserver fires once per cell during a column resize with
    // `columnResizeMode: "onChange"`; coalesce to one update per frame.
    let raf = 0
    const scheduleUpdate = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    update()
    root.addEventListener("scroll", update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(root)
    const mo = new MutationObserver(scheduleUpdate)
    mo.observe(root, { childList: true, subtree: true })
    return () => {
      root.removeEventListener("scroll", update)
      ro.disconnect()
      mo.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  })
</script>

<div bind:this={wrapper} class="relative w-full overflow-hidden {className}">
  {@render children()}
  {#if canScrollLeft}
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 left-0 z-[2] w-3 bg-gradient-to-r from-black/20 to-transparent"
    ></div>
  {/if}
  {#if canScrollRight}
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 right-0 z-[2] w-3 bg-gradient-to-l from-black/20 to-transparent"
    ></div>
  {/if}
</div>
